import { supabase } from '../supabaseClient';
import { minutesSince } from '../utils/dbTime';

/**
 * GPS Tracking Service
 * Reads real GPS data from Supabase gps_locations table
 * Written by driver app every 30s → latitude, longitude, timestamp, truck_id
 */

export const isGPSConfigured = () => true; // always true — real data only

/**
 * Get GPS status label and color based on age of last ping
 */
export const getGPSStatus = (ageMinutes) => {
  if (ageMinutes === null || ageMinutes === undefined) return { label: 'NO DATA', color: '#7f8c8d', dot: '⚫' };
  if (ageMinutes <= 5)  return { label: 'LIVE',    color: '#16c79a', dot: '🟢' };
  if (ageMinutes <= 30) return { label: 'STALE',   color: '#f39c12', dot: '🟡' };
  return                       { label: 'OFFLINE', color: '#e74c3c', dot: '🔴' };
};

/**
 * Fetch latest GPS ping per truck from Supabase
 * Returns map of truckId → location object
 */
export const fetchRealGpsLocations = async (trucks) => {
  try {
    if (!trucks || trucks.length === 0) return {};
    const truckIds = trucks.map(t => t.id).filter(Boolean);

    const { data, error } = await supabase
      .from('gps_locations')
      .select('truck_id, driver_id, latitude, longitude, speed_kmh, heading, accuracy_meters, timestamp')
      .in('truck_id', truckIds)
      .order('timestamp', { ascending: false });

    if (error) { console.error('GPS fetch error:', error); return {}; }

    // Keep only most recent ping per truck
    const latest = {};
    (data || []).forEach(row => {
      if (row.truck_id && !latest[row.truck_id]) latest[row.truck_id] = row;
    });

    const locations = {};
    trucks.forEach(truck => {
      const row = latest[truck.id];
      // gps_locations.timestamp is naive UTC; new Date() would read it as local
      // time and a 4 h-old ping came out LIVE. minutesSince() parses it as UTC.
      const ageMinutes = row ? Math.max(0, minutesSince(row.timestamp) ?? 0) : null;
      const status = getGPSStatus(ageMinutes);
      locations[truck.id] = {
        truckId: truck.id,
        truckNumber: truck.truck_number,
        licensePlate: truck.license_plate,
        lat: row ? row.latitude : null,
        lng: row ? row.longitude : null,
        speed_kmh: row ? (row.speed_kmh || 0) : null,
        heading: row ? (row.heading || 0) : null,
        accuracy: row ? row.accuracy_meters : null,
        timestamp: row ? row.timestamp : null,
        // __OFF_SHIFT_BOARD__ whose ping this is (drivers.id), for the off-shift check.
        driverId: row && row.driver_id != null ? String(row.driver_id) : null,
        ageMinutes,
        status: status.label,
        statusColor: status.color,
        statusDot: status.dot,
        hasData: !!row,
      };
    });

    return locations;
  } catch (err) {
    console.error('fetchRealGpsLocations error:', err);
    return {};
  }
};

/**
 * Calculate distance between two coordinates (Haversine)
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const calculateETA = (fromLat, fromLng, toLat, toLng, avgSpeedKmh = 80) => {
  const distance = calculateDistance(fromLat, fromLng, toLat, toLng);
  const minutes = Math.round((distance / avgSpeedKmh) * 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const formatCoordinates = (lat, lng) => {
  if (!lat || !lng) return 'No GPS';
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}${latDir}, ${Math.abs(lng).toFixed(4)}${lngDir}`;
};

export const getStatusColor = (status) => {
  const colors = { LIVE: '#16c79a', STALE: '#f39c12', OFFLINE: '#e74c3c', 'NO DATA': '#7f8c8d' };
  return colors[status] || colors['NO DATA'];
};

export const isWithinGeofence = (truckLat, truckLng, targetLat, targetLng, radiusKm = 0.5) =>
  calculateDistance(truckLat, truckLng, targetLat, targetLng) <= radiusKm;

export default { isGPSConfigured, fetchRealGpsLocations, calculateDistance, calculateETA,
  formatCoordinates, getStatusColor, getGPSStatus, isWithinGeofence };
